<template>
  <div
    :id="module.id"
    class="cards"
    :style="{
      backgroundColor: module.config.general.moduleBackgroundColor.value,
    }"
  >
    <GlobalUIMedia
      :media="module.config.general.media"
      v-if="module.config.general.showBackgroundMedia"
      :lazy="lazy"
    />
    <div
      :class="cardsContainerElClass"
      ref="cardsContainerEl"
      :style="[{ ...cardsContainerElVarStyles }, modulePadding]"
    >
      <Card
        v-for="card in cards"
        :id="card.id"
        :key="card.id"
        :module="card"
        :parentModule="module"
        :lazy="lazy"
        @emittedClick="$emit('emittedClick', { module, child: card })"
      />
    </div>
  </div>
</template>

<script>
import Card from "../Card/Card.vue";
import cardsModule from "./cardsDefaultModuleProp";
import GlobalUIMedia from "../../GlobalUI/GlobalUIMedia.vue";

import { globalComputed } from "../../GlobalUI/mixins/global.js";

export default {
  name: "Cards",
  mixins: [globalComputed],
  components: {
    Card,
    GlobalUIMedia,
  },
  props: {
    module: {
      type: Object,
      default() {
        return cardsModule;
      },
    },
    lazy: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isDynamicTemplate() {
      const { template } = this.module.config.general;

      return template.component === "CardDynamic";
    },
    cards() {
      return this.module.modules;
    },
    cardsContainerElClass() {
      return this.isDynamicTemplate
        ? "cards-container-dynamic"
        : "cards-container";
    },
    cardsContainerElVarStyles() {
      const { template } = this.module.config.general;

      let activeColumnsCount =
        template.component === "CardPortraitSmall"
          ? "--columns-count-portrait-small"
          : "--columns-count-other";

      return this.isDynamicTemplate
        ? null
        : {
            "--no-of-column-gaps": `calc(var(${activeColumnsCount}) - 1)`,
            "--card-width": `calc(var(--available-width) / var(${activeColumnsCount}))`,
          };
    },
  },
};
</script>

<style lang="scss" scoped>
@include reset;

.cards {
  container-type: inline-size;
  contain: layout;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  width: 100%;
}

.cards-container {
  --columns-count-other: 2;
  --columns-count-portrait-small: 4;
  --single-gap: 16px;
  --total-width: 100%;
  --no-of-column-gaps: calc(var(--columns-count-other) - 1);
  --total-columns-gap-size: calc(var(--no-of-column-gaps) * var(--single-gap));
  --available-width: calc(var(--total-width) - var(--total-columns-gap-size));
  --card-width: calc(var(--available-width) / var(--columns-count-other));

  display: flex;
  flex-wrap: wrap;
  gap: var(--single-gap);
  justify-content: center;

  width: 100%;
  max-width: 1600px;
  padding-inline: var(--cr-module-padding-large);

  @include laptop {
    --columns-count-portrait-small: 3;
  }

  @include tablet {
    --columns-count-other: 1;
    --columns-count-portrait-small: 2;

    padding-inline: calc(var(--cr-module-padding-large) / 2);
  }

  @include mobile {
    --columns-count-portrait-small: 1;

    padding-inline: 25px;
  }
}

@mixin with-count($n) {
  @if $n == 1 {
    &:first-child:nth-last-child(1) {
      @content;
    }
  } @else {
    &:first-child:nth-last-child(#{$n}),
    &:first-child:nth-last-child(#{$n}) ~ & {
      @content;
    }
  }
}

.cards-container-dynamic {
  --gap-width: 16px;

  display: flex;
  flex-wrap: wrap;
  gap: var(--gap-width);
  justify-content: center;

  width: 100%;
  max-width: 1600px;
  margin-inline: var(--cr-module-padding-large);

  @include tablet {
    margin-inline: calc(var(--cr-module-padding-large) / 2);
  }

  @include mobile {
    margin-inline: 25px;
  }
}

.card-dynamic {
  @include with-count(odd) {
    flex-basis: calc(33% - var(--gap-width));

    @include tablet {
      flex-basis: calc(50% - var(--gap-width));
    }

    @include mobile {
      flex-basis: 100%;
    }
  }

  @include with-count(even) {
    flex-basis: calc(50% - var(--gap-width));

    @include tablet {
      flex-basis: calc(50% - var(--gap-width));
    }

    @include mobile {
      flex-basis: 100%;
    }
  }

  @include with-count(8) {
    flex-basis: calc(33% - var(--gap-width));

    @include tablet {
      flex-basis: calc(50% - var(--gap-width));
    }

    @include mobile {
      flex-basis: 100%;
    }
  }

  @include with-count(3) {
    flex-basis: calc(33% - 10px);

    @include tablet {
      flex-basis: calc(50% - var(--gap-width));
    }

    @include mobile {
      flex-basis: 100%;
    }
  }

  @include with-count(1) {
    flex-basis: 100%;
  }
}
</style>
